import {
  createContext, useContext, useEffect, useMemo, useState, type ReactNode,
} from 'react';
import { api, fijarToken, alExpirarSesion } from './api/cliente';
import { precargarEtiquetas, olvidarEtiquetas } from './etiquetas';
import type { RespuestaAcceso, Usuario } from './api/tipos';

/**
 * Sesión de la aplicación.
 *
 * El token de acceso vive sólo en memoria, nunca en localStorage: si la
 * aplicación tuviera un XSS, un token en localStorage se puede leer y
 * llevar. La continuidad entre recargas la da la cookie de refresh, que
 * es httpOnly y JavaScript no puede tocar.
 */

interface Contexto {
  usuario: Usuario | null;
  cargando: boolean;
  acceder: (correo: string, password: string) => Promise<RespuestaAcceso>;
  verificarMfa: (tokenParcial: string, codigo: string) => Promise<RespuestaAcceso>;
  altaMfa: (tokenParcial: string, codigo: string) => Promise<RespuestaAcceso>;
  cambioInicial: (tokenParcial: string, nueva: string) => Promise<RespuestaAcceso>;
  salir: () => Promise<void>;
  puede: (modulo: string, accion: string) => boolean;
}

const SesionContexto = createContext<Contexto | null>(null);

export function ProveedorSesion({ children }: { children: ReactNode }) {
  const [usuario, setUsuario] = useState<Usuario | null>(null);
  const [cargando, setCargando] = useState(true);

  // Al abrir la aplicación se intenta recuperar la sesión con la cookie.
  // Si no hay ninguna, el fallo es esperado y no se muestra como error.
  useEffect(() => {
    let vigente = true;

    api.post<RespuestaAcceso>('/auth/refresh')
      .then((r) => {
        if (!vigente) return;
        if (r.accessToken && r.usuario) {
          fijarToken(r.accessToken);
          setUsuario(r.usuario);
          precargarEtiquetas();
        }
      })
      .catch(() => undefined)
      .finally(() => { if (vigente) setCargando(false); });

    return () => { vigente = false; };
  }, []);

  useEffect(() => {
    alExpirarSesion(() => {
      fijarToken(null);
      setUsuario(null);
      olvidarEtiquetas();
    });
  }, []);

  const valor = useMemo<Contexto>(() => {
    const aplicar = (r: RespuestaAcceso): RespuestaAcceso => {
      if (r.estado === 'autenticado' && r.accessToken && r.usuario) {
        fijarToken(r.accessToken);
        setUsuario(r.usuario);
        // Los colores de los estatus se traen ya: la primera tabla que se
        // pinte los tiene, sin el parpadeo de gris a color.
        precargarEtiquetas();
      }
      return r;
    };

    return {
      usuario,
      cargando,

      acceder: (correo, password) =>
        api.post<RespuestaAcceso>('/auth/login', { correo, password }).then(aplicar),

      verificarMfa: (tokenParcial, codigo) =>
        api.post<RespuestaAcceso>('/auth/mfa', { tokenParcial, codigo }).then(aplicar),

      altaMfa: (tokenParcial, codigo) =>
        api.post<RespuestaAcceso>('/auth/mfa/alta-inicial', { tokenParcial, codigo })
          .then(aplicar),

      cambioInicial: (tokenParcial, nueva) =>
        api.post<RespuestaAcceso>('/auth/cambio-inicial', { tokenParcial, nueva }).then(aplicar),

      salir: async () => {
        await api.post('/auth/logout').catch(() => undefined);
        fijarToken(null);
        setUsuario(null);
        // Otra cuenta puede tener otros catálogos a la vista.
        olvidarEtiquetas();
      },

      // La pantalla oculta lo que no se puede hacer, pero quien decide de
      // verdad es la API: esto es comodidad, no seguridad.
      puede: (modulo, accion) => usuario?.permisos.includes(`${modulo}:${accion}`) ?? false,
    };
  }, [usuario, cargando]);

  return <SesionContexto.Provider value={valor}>{children}</SesionContexto.Provider>;
}

export function useSesion(): Contexto {
  const contexto = useContext(SesionContexto);
  if (!contexto) throw new Error('useSesion se usó fuera de ProveedorSesion.');
  return contexto;
}
